import {
  Box,
  FormControl,
  FormHelperText,
  FormLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  TextField,
  ToggleButton,
  Tooltip,
} from '@mui/material';
import { INIT_DATA as INIT_ADDRESS_DATA } from '@features/workspace/components/users/components/address-fields';
import {
  identityTypeOptions,
  reasonForNoIdOptions,
  titleOptions,
} from '@features/workspace/components/users/utils/user-management';
import { employeePositionOptions } from '@features/workspace/views/company/utils/company-management';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import UseFormToggle from '@components/use-form-toggle';
import { Controller } from 'react-hook-form';
import { InfoOutlined as InfoOutlinedIcon } from '@mui/icons-material';
import { useAppSelector } from '@store/hooks';
import { selectIsSupportUser } from '@store/features/auth';
import { EmployeePositionEnum, IdentityTypeEnum } from '@utils/constants';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { FC, useMemo } from 'react';
import { parsePhoneNumber } from 'libphonenumber-js';
import { ensureFirstPlus } from '@utils/formaters';
import { countryOptions } from '@utils/countries-provider';
import UseFormSelect from '@components/use-form-select';
import { IUser } from '../../../types';

export const INIT_DATA: IUser & { position: EmployeePositionEnum } = {
  position: EmployeePositionEnum.Director, // Default to Not a major shareholder director
  person: {
    firstName: '',
    lastName: '',
    title: 'Mr',
    dateOfBirth: '',
    gender: 0,
    nationality: '',
    isPep: false,
    sisAbsentZAIDReason: null,
    identityDocuments: [
      {
        idNumber: '',
        identityType: IdentityTypeEnum.SAIdNumber, // default to SA ID type
      },
    ],
  },
  phoneNumber: '',
  email: '',
  address: INIT_ADDRESS_DATA,
};

const UserFields: FC<any> = ({
  data,
  isAuthPerson,
  register,
  errors,
  control,
  openChangeEmail,
  pendingEmail,
}) => {
  const isSupportUser = useAppSelector(selectIsSupportUser);

  const initialPhoneCountryCode = useMemo(() => {
    return data?.phoneNumber
      ? parsePhoneNumber(
          ensureFirstPlus(data?.phoneNumber)
        ).country?.toLowerCase()
      : 'za';
  }, []);

  return (
    <>
      <UseFormToggle
        exclusive
        fullWidth
        control={control}
        error={!!errors?.person?.title}
        helperText={errors?.person?.title?.message}
        label="Title"
        name="person.title"
        variant="outlined"
      >
        {titleOptions.map((option) => (
          <ToggleButton
            key={`title-${option.label}-${option.value}`}
            aria-label={option.label}
            value={option.value}
          >
            {option.label}
          </ToggleButton>
        ))}
      </UseFormToggle>

      <TextField
        fullWidth
        required
        label="First name"
        placeholder="First name"
        variant="outlined"
        {...register('person.firstName')}
        error={!!errors?.person?.firstName}
        helperText={errors?.person?.firstName?.message}
      />

      <TextField
        fullWidth
        required
        label="Last name"
        placeholder="Last name"
        variant="outlined"
        {...register('person.lastName')}
        error={!!errors?.person?.lastName}
        helperText={errors?.person?.lastName?.message}
      />

      {!isAuthPerson && (
        <UseFormToggle
          asNumber
          exclusive
          fullWidth
          control={control}
          error={!!errors?.position}
          helperText={errors?.position?.message}
          label="Are they a major shareholder (25%)?"
          name="position"
          variant="outlined"
        >
          {[0, 1].map((val) => (
            <ToggleButton
              key={`position-${val}`}
              aria-label={employeePositionOptions[val].label}
              value={employeePositionOptions[val].value}
            >
              {!val ? 'Yes' : 'No'}
            </ToggleButton>
          ))}
        </UseFormToggle>
      )}

      <UseFormToggle
        asBoolean
        exclusive
        fullWidth
        control={control}
        error={!!errors?.person?.isPep}
        helperText={errors?.person?.isPep?.message}
        label="Is politically exposed person?"
        name="person.isPep"
        variant="outlined"
      >
        {[true, false].map((val) => (
          <ToggleButton
            key={`isPep-${val}`}
            aria-label={val ? 'Yes' : 'No'}
            value={val}
          >
            {val ? 'Yes' : 'No'}
          </ToggleButton>
        ))}
      </UseFormToggle>

      <UseFormToggle
        asNumber
        exclusive
        fullWidth
        control={control}
        error={!!errors?.person?.identityDocuments?.[0]?.identityType}
        helperText={errors?.person?.identityDocuments?.[0]?.message}
        label="Type of identification"
        name="person.identityDocuments[0].identityType"
        variant="outlined"
      >
        {identityTypeOptions.map((option) => (
          <ToggleButton
            key={`identity-type-${option.value}`}
            aria-label={option.label}
            value={option.value}
          >
            {option.label}
          </ToggleButton>
        ))}
      </UseFormToggle>
      <UseFormSelect
        fullWidth
        control={control}
        disabled={
          data?.person?.identityDocuments?.[0].identityType ===
          IdentityTypeEnum.ForeignIdNumber
        }
        error={!!errors?.person?.sisAbsentZAIDReason}
        helperText={errors?.person?.sisAbsentZAIDReason?.message}
        label="Reason for no ID"
        name="person.sisAbsentZAIDReason"
        placeholder="Not applicable"
        variant="outlined"
      >
        {reasonForNoIdOptions.map((option) => (
          <MenuItem key={option.label} value={option.value as number}>
            {option.label}
          </MenuItem>
        ))}
      </UseFormSelect>
      <UseFormSelect
        fullWidth
        required
        control={control}
        error={!!errors?.person?.nationality}
        helperText={errors?.person?.nationality?.message}
        label="Nationality"
        name="person.nationality"
        placeholder="Nationality"
        variant="outlined"
      >
        {countryOptions.map((option: any) => (
          <MenuItem key={option.label} value={option.label}>
            {option.label}
          </MenuItem>
        ))}
      </UseFormSelect>

      <TextField
        fullWidth
        required
        label={
          data?.person?.identityDocuments?.[0]?.identityType ===
          IdentityTypeEnum.SAIdNumber
            ? 'South African ID number'
            : 'Passport number'
        }
        placeholder={
          data?.person?.identityDocuments?.[0]?.identityType ===
          IdentityTypeEnum.SAIdNumber
            ? 'South African ID number'
            : 'Passport number'
        }
        variant="outlined"
        {...register('person.identityDocuments[0].idNumber')}
        error={!!errors?.person?.identityDocuments?.[0]?.idNumber}
        helperText={errors?.person?.identityDocuments?.[0]?.idNumber?.message}
      />

      <FormControl error={!!errors?.phoneNumber}>
        <FormLabel>Phone number *</FormLabel>
        <Controller
          control={control}
          name="phoneNumber"
          render={({ field: { onChange, value } }) => (
            <PhoneInput
              country={initialPhoneCountryCode}
              inputStyle={{
                background: '#FFF',
                border: '1px solid #EAEAEA',
                borderRadius: '8px',
                height: '48px',
                color: '#4B4B4B',
                width: '100%',
              }}
              placeholder="Phone number"
              value={value}
              onChange={onChange}
            />
          )}
        />
        {errors.phoneNumber && (
          <FormHelperText>{errors?.phoneNumber?.message}</FormHelperText>
        )}
      </FormControl>

      <Controller
        control={control}
        name="person.dateOfBirth"
        render={({ field: { onChange, value } }) => (
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DesktopDatePicker
              inputFormat="YYYY-MM-DD"
              label="Date Of Birth"
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={!!errors?.person?.dateOfBirth}
                  helperText={errors?.person?.dateOfBirth?.message}
                />
              )}
              value={value}
              onChange={(event) => {
                onChange(event);
              }}
            />
          </LocalizationProvider>
        )}
      />

      {data?.id && (
        <FormControl>
          <InputLabel>Email address</InputLabel>
          <OutlinedInput
            disabled
            endAdornment={
              !!openChangeEmail && isSupportUser ? (
                <InputAdornment position="end">
                  <Box
                    alignItems="center"
                    display="flex"
                    style={{
                      cursor: 'pointer',
                    }}
                    onClick={openChangeEmail}
                  >
                    <Box alignItems="center" display="flex">
                      <i className="ri-pencil-line" style={{ fontSize: 14 }} />
                      &nbsp; Change Email
                    </Box>
                    {!!pendingEmail && pendingEmail !== data.email && (
                      <Box alignItems="center" display="flex" ml={2}>
                        <Tooltip
                          title={`Pending email change to "${pendingEmail}" is waiting for confirmation by email sent to the new address`}
                        >
                          <InfoOutlinedIcon />
                        </Tooltip>
                      </Box>
                    )}
                  </Box>
                </InputAdornment>
              ) : null
            }
            label="Password"
            type="text"
            value={data?.email}
          />
        </FormControl>
      )}

      {!data?.id && (
        <TextField
          fullWidth
          required
          label="Email address"
          placeholder="e.g. joe.soap@example.com"
          variant="outlined"
          {...register('email')}
          error={!!errors.email}
          helperText={errors?.email?.message}
        />
      )}
    </>
  );
};

export default UserFields;
